import { EventStatusBadge } from '@/components/events';
import { VisibilityBadge } from '@/components/shared';
import type { ChallengeType } from '@/config/objects/challenges';
import type { PlacedArtifact } from '@/config/objects/placements';
import { formatShortDate } from '@/lib/datetime';
import type { CrudConfig } from '@startupquickstart/frontend';

export type EventStatus = 'draft' | 'scheduled' | 'active' | 'paused' | 'completed' | 'archived';
export type Visibility = 'private' | 'unlisted' | 'public';
export type Difficulty = 'easy' | 'medium' | 'hard' | 'expert';
// Challenge type is the API enum — single-sourced from
// `challengeKinds.ts` via `config/objects/challenges.tsx`. The stale
// admin-only union (`trivia`, `location`, `task`, …) is gone; legacy
// values that may still exist in old rows are handled by the editor's
// type <Select> guard (renders the unknown value as a disabled option).
export type { ChallengeType };

/**
 * Per-event map-icon overrides keyed by challenge type (DT-165
 * phase B). The keys here are the *API* challenge-type enum values
 * (riddle/counting/exploration/search/observation/photo/video/finale/
 * puzzle), not the loose admin-side type used in `Challenge.type`.
 * Both `iconUrl` and `color` are independently optional inside each
 * entry; the mobile resolver treats a missing entry, an empty entry,
 * or null fields as "no event-level override for this type".
 */
export interface ChallengeTypeIconEntry {
  iconUrl?: string | null;
  /** Lucide icon name (PascalCase, e.g. "TreePine"); `iconUrl` wins when both are set. */
  icon?: string | null;
  color?: string | null;
}
export type ChallengeTypeIcons = Partial<Record<string, ChallengeTypeIconEntry>>;
export type AnswerType =
  | 'text'
  | 'photo'
  | 'video'
  | 'true_false'
  | 'multiple_choice'
  | 'fill_in_the_blank'
  | 'puzzle';
export type GroupUnlockMode = 'all_at_once' | 'sequential';
export type GroupUnlockCondition = 'starter' | 'after_challenge' | 'after_group';

export interface Event {
  id: string;
  organizationId: string;
  templateId?: string | null;
  name: string;
  description?: string | null;
  coverImageUrl?: string | null;
  /** Finish line — where teams return at the end. Deadline is `endTime`. */
  finishVenueName?: string | null;
  finishAddress?: string | null;
  finishLatitude?: number | null;
  finishLongitude?: number | null;
  finishContactPhone?: string | null;
  introVideoUrl?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  /**
   * When set, the event spans this many calendar days starting at
   * `startTime`. Mobile renders "Day X of N" headers; the API derives
   * `endTime` from this on save so both stay consistent.
   */
  durationDays?: number | null;
  timezone: string;
  status: EventStatus;
  visibility: Visibility;
  settings?: Record<string, unknown> | null;
  leaderboardVisible: boolean;
  teamChatEnabled: boolean;
  /** AI photo/video verdicts (opt-in — each verdict costs tokens). */
  aiValidationEnabled?: boolean;
  /** Hold photo/video points until a facilitator approves. */
  requireMediaReview?: boolean;
  helpChatEnabled: boolean;
  quizMode: boolean;
  /**
   * Attempts a team gets on an answer-checked challenge before it closes,
   * reveals the answer and scores nothing. null = unlimited. Individual
   * challenges can override.
   */
  defaultMaxAttempts?: number | null;
  /** When true, per-challenge time limits actually expire a challenge. */
  enforceTimeLimits?: boolean;
  /** Corporate team play: only the device holding the team can score. */
  requireSingleDevice?: boolean;
  /** Split teams cannot score until they regroup. */
  requireTeamTogether?: boolean;
  teamTogetherRadiusMeters?: number;
  // Branding (DT-162). Mobile + display surfaces apply these on join
  // so corporate clients can ship a branded experience without a
  // custom build. Each field is null/empty when the event hasn't been
  // branded — clients fall back to the default tokens.
  logoUrl?: string | null;
  logoSmallUrl?: string | null;
  backgroundColor?: string | null;
  accentColor?: string | null;
  theme?: string;
  customPointsLabel?: string | null;
  hidePoiOrder?: boolean;
  /**
   * DT-157. When true, mobile lets teams tap a challenge pin on the
   * map to activate it directly (mirrors MooveTeam's
   * PermitirActivarPruebasPulsando).
   */
  permitActivateOnPin?: boolean;
  /**
   * DT-166. Seconds the post-event slideshow holds each photo before
   * fading to the next. Default 5; valid range 2–60.
   */
  slideshowIntervalSeconds?: number;
  /**
   * Per-event default map-icon overrides keyed by challenge type
   * (DT-165 phase B). Each entry has independently-optional
   * `iconUrl` + `color` so an event can recolor without uploading
   * new images, or vice versa. `null` when no overrides configured.
   */
  challengeTypeIcons?: ChallengeTypeIcons | null;
  startedAt?: string | null;
  endedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  template?: {
    id: string;
    name: string;
  } | null;
  _count?: {
    teams: number;
    challenges: number;
  };
}

// Event Challenge - copied from library, can be customized
export interface EventChallenge {
  id: string;
  eventId: string;
  sourceId?: string | null;
  groupId?: string | null;
  name: string;
  description?: string | null;
  clue: string;
  /** Canonical correct answer (text/true_false/multiple_choice/fill_in_the_blank). */
  answer?: string | null;
  /** Additional accepted answers — used heavily by fill_in_the_blank. */
  alternateAnswers?: string[];
  /** Multiple-choice options (only meaningful when answerType = multiple_choice). */
  choices?: string[];
  /** Free-form context the AI validator uses for photo/video grading. */
  aiContext?: string | null;
  answerType?: AnswerType;
  hint?: string | null;
  hintCost: number;
  points: number;
  difficulty: Difficulty;
  type: ChallengeType;
  zone?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  requiresPhoto: boolean;
  sortOrder: number;
  isStarter: boolean;
  requireAll: boolean;
  isCustomized: boolean;
  maxCompletions?: number | null;
  /**
   * Wrong answers allowed before the challenge closes. `null` inherits the
   * event's `defaultMaxAttempts`; `0` means unlimited.
   */
  maxAttempts?: number | null;
  /**
   * Per-wrong-attempt scoring penalty. The two forms are mutually exclusive
   * — flat points off, or a percentage of the challenge's points.
   */
  attemptPenaltyPoints?: number | null;
  attemptPenaltyPercent?: number | null;
  /** Optional typed code that gates this challenge until a team enters it. */
  accessCode?: string | null;
  /** Custom prompt copy shown above the access-code input on mobile. */
  accessCodeMessage?: string | null;
  speedBonuses: number[];
  imageUrl?: string | null;
  videoUrl?: string | null;
  audioUrl?: string | null;
  /** Per-challenge map-icon color override (DT-165). Hex or null. */
  mapIconColor?: string | null;
  /** Per-challenge custom icon image (DT-165 phase B). URL or null. */
  mapIconUrl?: string | null;
  /** Facilitator-supervised flag (DT-164). Implied by type === 'roadblock'. */
  requiresFacilitator?: boolean;
  /** Copy shown to teams + facilitators on supervised challenges. */
  facilitatorInstructions?: string | null;
  /** Grid size for puzzle-type challenges (NxN). */
  puzzleGridSize?: number | null;
  isTimed?: boolean;
  /** Seconds allowed once the timer starts. null/0 = untimed. */
  timeLimitSeconds?: number | null;
  /**
   * Per-type minigame config (DT-156). Shape varies by `type` and is
   * validated server-side against the minigame registry. Treated as
   * opaque on the wire; the authoring panels in
   * `@/components/challenges/minigames/` parse + render the right form.
   */
  minigameConfig?: Record<string, unknown> | null;
  /**
   * Activation gate (DT-157). Controls how a challenge becomes
   * playable for a team beyond the standard group-unlock rules.
   * `available` (default) preserves today's behaviour. Phase A
   * lights up `manual`; later phases wire `gps_proximity`,
   * `qr_code`, and `ibeacon`.
   */
  activationMode?: 'available' | 'manual' | 'gps_proximity' | 'qr_code' | 'ibeacon';
  /** Meters within which the team must be standing (gps_proximity). */
  activationRadius?: number | null;
  /**
   * Auto-generated per-challenge QR identifier (DT-187 phase C).
   * Encoded into the printable QR; matched by the activate endpoint
   * for `qr_code` mode. Generated server-side on save.
   */
  qrCodeId?: string | null;
  /** iBeacon UUID/major/minor (phase D, stored now). */
  ibeaconUuid?: string | null;
  ibeaconMajor?: number | null;
  ibeaconMinor?: number | null;
  createdAt: string;
  updatedAt: string;
  group?: {
    id: string;
    name: string;
    color?: string | null;
  } | null;
}

// Event Group Unlock Condition
export interface EventGroupUnlock {
  id: string;
  groupId: string;
  conditionType: GroupUnlockCondition;
  triggerChallengeId?: string | null;
  triggerGroupId?: string | null;
  triggerChallenge?: {
    id: string;
    name: string;
  } | null;
  triggerGroup?: {
    id: string;
    name: string;
  } | null;
}

// Event Challenge Group
export interface EventChallengeGroup {
  id: string;
  eventId: string;
  sourceId?: string | null;
  name: string;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
  sortOrder: number;
  unlockMode: GroupUnlockMode;
  isWelcomeGroup: boolean;
  createdAt: string;
  updatedAt: string;
  challenges: EventChallenge[];
  unlockConditions: EventGroupUnlock[];
}

// Event Artifact - placed in an event from library. The owner-agnostic body
// lives in `placements.ts` (shared with TemplateArtifact); this adds the FK.
export interface EventArtifact extends PlacedArtifact {
  eventId: string;
}

export const eventCrudConfig: CrudConfig<Event> = {
  entityName: 'Event',
  entityNamePlural: 'Events',

  endpoints: {
    list: '/events',
    single: (id: string) => `/events/${id}`,
    create: '/events',
    update: (id: string) => `/events/${id}`,
    delete: (id: string) => `/events/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter event name',
      validation: {
        min: 2,
        max: 255,
      },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      placeholder: 'Enter a description',
    },
    {
      key: 'templateId',
      label: 'Template',
      type: 'text',
      required: false,
      placeholder: 'Select a template',
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: 'draft',
      options: [
        { label: 'Draft', value: 'draft' },
        { label: 'Scheduled', value: 'scheduled' },
        { label: 'Active', value: 'active' },
        { label: 'Paused', value: 'paused' },
        { label: 'Completed', value: 'completed' },
        { label: 'Archived', value: 'archived' },
      ],
      format: (value: string) => {
        if (!value) return null;
        return <EventStatusBadge status={value as EventStatus} />;
      },
    },
    {
      key: 'visibility',
      label: 'Visibility',
      type: 'select',
      required: true,
      defaultValue: 'private',
      options: [
        { label: 'Private', value: 'private' },
        { label: 'Unlisted', value: 'unlisted' },
        { label: 'Public', value: 'public' },
      ],
      format: (value: string) => {
        if (!value) return null;
        return <VisibilityBadge visibility={value as Visibility} />;
      },
    },
    {
      key: 'coverImageUrl',
      label: 'Cover Image',
      type: 'image',
      required: false,
    },
    {
      key: 'finishVenueName',
      label: 'Finish Venue',
      type: 'text',
      required: false,
      placeholder: 'e.g. The Liberty Hotel',
      helpText: 'Where teams return at the end. The return deadline is the event End Time.',
    },
    {
      key: 'finishAddress',
      label: 'Finish Address',
      type: 'text',
      required: false,
      placeholder: 'Street address',
    },
    {
      key: 'finishLatitude',
      label: 'Finish Latitude',
      type: 'number',
      required: false,
      helpText: 'Needed for the finish pin and the Navigate button.',
    },
    {
      key: 'finishLongitude',
      label: 'Finish Longitude',
      type: 'number',
      required: false,
    },
    {
      key: 'finishContactPhone',
      label: 'Finish Contact Phone',
      type: 'text',
      required: false,
      placeholder: '+1 617 555 0134',
      helpText: 'Shown to teams as a Call button for when something goes wrong.',
    },
    {
      key: 'introVideoUrl',
      label: 'Intro Video',
      type: 'video',
      required: false,
    },
    {
      key: 'startTime',
      label: 'Start Time',
      type: 'datetime',
      required: false,
      // List cells: draft events have no schedule yet — read "Not scheduled"
      // in muted text rather than a bare dash so the empty state is legible.
      format: (value: unknown) =>
        value ? (
          formatShortDate(value as string)
        ) : (
          <span className="text-muted-foreground">Not scheduled</span>
        ),
    },
    {
      key: 'endTime',
      label: 'End Time',
      type: 'datetime',
      required: false,
    },
    {
      // Virtual list-only column: the events list response includes
      // `_count.teams`, surfaced here as a right-aligned tabular figure.
      key: '_count',
      label: 'Teams',
      type: 'number',
      align: 'right',
      format: (_value: unknown, item?: Event) => (
        <span className="font-mono tabular-nums text-muted-foreground">
          {item?._count?.teams ?? 0}
        </span>
      ),
    },
    {
      key: 'durationDays',
      label: 'Duration (days)',
      type: 'number',
      required: false,
      placeholder: 'e.g., 3 for a 3-day event',
      validation: { min: 1, max: 365 },
    },
    {
      key: 'defaultMaxAttempts',
      label: 'Attempts per Challenge',
      type: 'number',
      required: false,
      placeholder: 'Leave empty for unlimited',
      helpText:
        'Wrong answers a team may give on an answer-checked challenge before it closes, reveals the answer and scores nothing. Individual challenges can override this.',
      validation: { min: 1, max: 99 },
    },
    {
      key: 'timezone',
      label: 'Timezone',
      type: 'text',
      required: true,
      defaultValue: 'UTC',
      placeholder: 'e.g., America/New_York',
    },
    {
      key: 'startedAt',
      label: 'Started At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'endedAt',
      label: 'Ended At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
      // Compact, scannable date for the list ("Jun 10, 2026") instead of the
      // verbose default locale timestamp.
      format: (value: unknown) => formatShortDate(value as string),
    },
  ],

  listView: {
    fields: ['name', 'status', 'visibility', '_count', 'startTime', 'updatedAt'],
    // Phones get name + status (with the lifecycle shortcut); the rest
    // is desktop detail — without hideOnMobile the table overflows into
    // a sideways scroll that reads as clipped.
    columns: [
      { field: 'name' },
      { field: 'status' },
      { field: 'visibility', hideOnMobile: true },
      { field: '_count', hideOnMobile: true },
      { field: 'startTime', hideOnMobile: true },
      { field: 'updatedAt', hideOnMobile: true },
    ],
    searchableFields: ['name', 'description'],
    sortableFields: [
      'name',
      'status',
      'visibility',
      'startTime',
      'endTime',
      'createdAt',
      'updatedAt',
    ],
    defaultSort: {
      field: 'updatedAt',
      direction: 'desc',
    },
    itemsPerPage: 10,
    // Saved lists + the filter panel are intentionally disabled for now
    // (search + column sorting only). Filter definitions live in git history
    // (PR #369-era configs) — restore them here to re-enable both.
    filters: [],
    actions: {
      create: { enabled: true },
      view: { enabled: true },
      edit: { enabled: true },
      delete: { enabled: true },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Event Details',
        description: 'Basic event information',
        fields: ['status', 'visibility', 'templateId'],
        defaultExpanded: true,
      },
      {
        title: 'Description',
        description: 'Event description',
        fields: ['description'],
        defaultExpanded: true,
      },
      {
        title: 'Schedule',
        description: 'Event timing',
        fields: ['startTime', 'endTime', 'durationDays', 'timezone', 'startedAt', 'endedAt'],
        defaultExpanded: true,
      },
      {
        title: 'Media',
        description: 'Cover image and intro video',
        fields: ['coverImageUrl', 'introVideoUrl'],
      },
      {
        title: 'Finish Location',
        description: 'Where teams return at the end of the hunt',
        fields: [
          'finishVenueName',
          'finishAddress',
          'finishLatitude',
          'finishLongitude',
          'finishContactPhone',
        ],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: false,
      },
    ],
  },

  form: {
    sections: [
      {
        title: 'Event Details',
        description: 'Basic information about the event',
        fields: ['name', 'description', 'templateId', 'coverImageUrl', 'introVideoUrl'],
        columns: 1,
      },
      {
        title: 'Schedule',
        description:
          'When the event takes place. Set Duration (days) for multi-day events — End Time will be derived automatically.',
        fields: ['startTime', 'endTime', 'durationDays', 'timezone'],
        columns: 2,
      },
      {
        title: 'Finish Location',
        description:
          'Where teams return at the end. Leave blank for a hunt with no fixed finish — the app then shows no finish pin. The return deadline is the event End Time.',
        fields: [
          'finishVenueName',
          'finishAddress',
          'finishLatitude',
          'finishLongitude',
          'finishContactPhone',
        ],
        columns: 2,
      },
      {
        title: 'Settings',
        description: 'Event visibility and status',
        fields: ['status', 'visibility', 'defaultMaxAttempts'],
        columns: 2,
      },
    ],
    validation: {
      name: { required: true, min: 2, max: 255 },
    },
  },
};
